import type { MemoizedFn } from 'memoize-one';
import memoize from 'memoize-one';
import uuid from 'uuid/v4';

import { createExperienceTrackerAnalyticsEventPayload } from '@atlassian/embedded-confluence/createExperienceTrackerAnalyticsEventPayload';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { fg } from '@confluence/feature-gating';
import { getOverride } from '@confluence/meta';
import type { MonitoringContext } from '@confluence/monitoring/entry-points/MonitoringClient.ts';
import {
	getLogSafeErrorAttributes,
	setContentErrorAttributes,
	setSlaErrorAttributes,
} from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { isModuleNotFoundError } from '@confluence/monitoring/entry-points/isModuleNotFoundError.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import type { ExperienceAttributes, ExperienceEvent } from './ExperienceEvent.ts';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	CREATE_INLINE_COMMENT_EXPERIENCE,
	CREATE_PAGE_EXPERIENCE,
	EDIT_PAGE_EXPERIENCE,
	FRONT_COVER_LOAD_EXPERIENCE,
	SELECT_INLINE_COMMENT_EXPERIENCE,
	SPACE_OVERVIEW_EXPERIENCE,
	VIEW_CONTENT_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
} from './ExperienceName.ts';
import { ExperienceTimeoutError } from './ExperienceTimeoutError.ts';
// oxlint-disable-next-line import/no-cycle
import { ExperienceTracker, type ExperienceTrackerAPI } from './ExperienceTracker.ts';
// eslint-disable-next-line import/no-cycle
import { startViewPageSessionExperience } from './viewPageSessionExperience.ts';

type ActiveSlaExperienceProperties = Pick<ExperienceEvent, 'id' | 'attributes'>;

const SLA_EXPERIENCES = new Set<string>([
	// Currently not SLA experience, but will be in the future
	SELECT_INLINE_COMMENT_EXPERIENCE,
	CREATE_INLINE_COMMENT_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	// SLA experiences ⬇️
	ADD_PAGE_COMMENT_EXPERIENCE,
	CREATE_PAGE_EXPERIENCE,
	EDIT_PAGE_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
]);

const activeSlaExperiences = new Map<string, ActiveSlaExperienceProperties>();
const activeExperiences = new Set<string>();
const currentRoute = {
	routeName: '',
	successfulExperiences: new Set<string>(),
};
const previousRoute = {
	routeName: '',
	successfulExperiences: new Set<string>(),
};

// Exported for testing
export function captureExperience({ action, name, id, attributes = {} }: ExperienceEvent): void {
	const isSlaExperience = SLA_EXPERIENCES.has(name);
	if (action === 'taskStart') {
		if (isSlaExperience) {
			// Create a unique experience ID for request headers
			attributes.uniqueExperienceId = uuid();
			activeSlaExperiences.set(name, { id, attributes });
		}
		activeExperiences.add(name);
	} else {
		if (isSlaExperience) {
			// Extract unique experience ID to attach to event attributes for analytics/monitoring logs
			attributes.uniqueExperienceId =
				activeSlaExperiences.get(name)?.attributes?.uniqueExperienceId;
			activeSlaExperiences.delete(name);
		}
		activeExperiences.delete(name);

		if (action === 'taskSuccess') {
			currentRoute.successfulExperiences.add(name);
		}
	}

	const slaExperience = getLatestActiveSlaExperience();
	const experiences = activeExperiences.size > 0 ? activeExperiences : null;
	if (slaExperience || experiences) {
		const updatedContext: Partial<MonitoringContext> = {};
		if (experiences) {
			updatedContext.activeExperiences = activeExperiences;
		}
		if (slaExperience) {
			updatedContext.slaExperience = slaExperience.name;
		}

		getMonitoringClient().updateContext(updatedContext);
	}
}

export function getLatestActiveSlaExperience():
	| ({ name: string } & ActiveSlaExperienceProperties)
	| null {
	if (activeSlaExperiences.size === 0) {
		return null;
	}
	const experienceEntry = Array.from(activeSlaExperiences).pop()!;
	return {
		...experienceEntry[1],
		name: experienceEntry[0],
	};
}

export const getExperienceTracker: MemoizedFn<() => ExperienceTracker> = memoize(
	(): ExperienceTracker => {
		const tracker = new ExperienceTracker();

		// Do nothing for search engine crawlers
		if (/bot/i.test(window.navigator.userAgent)) {
			return tracker;
		}

		tracker.subscribe((event) => {
			//
			// View Page SLA Split
			//
			if (event.name === VIEW_CONTENT_EXPERIENCE) {
				splitViewContentExperience(tracker, event);
				// View-content was introduced only to split space-overview out of
				// view-page, because view-page is covered by Premium SLA and
				// space-overview was not satisfactorily reliable. View-content is not
				// allowlisted in cc-fe-telemetry so submitting through MonitoringClient
				// below is a waste. Sending through AnalyticsWebClient is a waste too
				// since view-content is not tracked as an SLO and it could be derived
				// from view-page and space-overview which are tracked as separate SLOs
				// already anyway. Splunk found 23 million view-content events in the last
				// 24 hours which is about 3% of experience-tracker events.
				return;
			}

			captureExperience(event);

			//
			// AnalyticsWebClient
			//
			void getAnalyticsWebClient().then((client) => {
				client.sendOperationalEvent(
					createExperienceTrackerAnalyticsEventPayload(provideGenericAttributes(event)),
				);
			});

			//
			// MonitoringClient
			//
			const isTimeoutError =
				(event.action === 'taskFail' && event?.error?.name === 'ExperienceTimeoutError') ||
				(event.action === 'taskAbort' &&
					typeof event?.reason === 'string' &&
					event.reason.startsWith('ExperienceTimeout'));
			const isPermissionError =
				event.name === EDIT_PAGE_EXPERIENCE &&
				event.action === 'taskAbort' &&
				(event.reason?.includes?.('401') || event.reason?.includes?.('403'));
			const tags = [
				`action:${event.action}`,
				`isActiveTab:${typeof document !== 'undefined' && document.visibilityState === 'visible'}`,
				...(event.action === 'taskFail' || event.action === 'taskAbort'
					? [`isTimeoutError:${isTimeoutError}`]
					: []),
				...(event.name === EDIT_PAGE_EXPERIENCE && event.action === 'taskAbort'
					? [`isPermissionError:${isPermissionError}`]
					: []),
			];

			if (event.attributes) {
				for (const a in event.attributes) {
					if (a === 'alertResponder' && fg('confluence_experience_responder_routing')) {
						// Only tag `alertResponder` for fail events. `alertResponder` to be used to help determine which team to send alerts to.
						// `attributes.alertResponder` is set on all fail events when appropriate. See confluence/next/packages/experience-tracker/src/Experience.ts
						if (event.action === 'taskFail') {
							tags.push(`alertResponder:${event.attributes[a]}`);
						}
						continue;
					}

					tags.push(`${a}:${event.attributes[a]}`);
				}
			}

			tags.push(`isMultiThread:${window?.__IS_MULTITHREADED_RUNTIME__}`);

			if (event.action === 'taskFail' && SLA_EXPERIENCES.has(event.name)) {
				// in this section, we submit information about SLA-affecting experience failures.
				// We don't submit timeout errors to monitoring systems however; they would be submitted to Analytics instead.

				// cc-fe-telemetry will filter these additional tags before sending to SFX, these are only for "Raw TelemetryEvent" Splunk logs
				if (event?.error?.message) {
					tags.push(`error:${event.error.message}`);
				}
				// Supply additional information from alternative sources, for case where SessionDataQuery is not available
				tags.push(`hostname:${window.location.hostname}`);
				tags.push(`tenantIdMeta:${getOverride('cloud-id')}`);

				// See https://product-fabric.atlassian.net/browse/PGXT-9417 — suppress module not found
				// errors from the experience-tracker counter so they don't skew SLA metrics in Splunk.
				if (!isModuleNotFoundError(event.error)) {
					getMonitoringClient().incrementCounter(`experience-tracker:${event.name}`, tags);

					if (!(event.error instanceof ExperienceTimeoutError)) {
						setContentErrorAttributes(event.error, {
							objectId: event.attributes?.contentId as string | null | undefined,
						});
						setSlaErrorAttributes(event.error, {
							failedSlaExperience: event.name,
							experienceId: (event.attributes?.uniqueExperienceId as string) ?? event.id,
						});
						getMonitoringClient().submitError(event.error, { attribution: 'sla' });
					}
				}
			} else {
				getMonitoringClient().incrementCounter(`experience-tracker:${event.name}`, tags);
			}
		});

		return tracker;
	},
);

export function stopExperienceOnError(
	name: string,
	error: Error,
	attributes?: ExperienceAttributes,
): void {
	getExperienceTracker().stopOnError({ name, error, attributes });
}

export const forwardExperience = (
	tracker: ExperienceTrackerAPI,
	event: ExperienceEvent,
	name: string = event.name,
): void => {
	switch (event.action) {
		case 'taskStart':
			tracker.start({ ...event, name });
			break;
		case 'taskSuccess':
			tracker.succeed({ ...event, name });
			break;
		case 'taskFail':
			tracker.fail({ ...event, name });
			break;
		case 'taskAbort':
			tracker.abort({ ...event, name });
	}
};

export function captureRouteChange(routeName: string): void {
	previousRoute.routeName = currentRoute.routeName;
	previousRoute.successfulExperiences = currentRoute.successfulExperiences;
	currentRoute.routeName = routeName;
	currentRoute.successfulExperiences = new Set<string>();
}

export function getPreviousRouteExperienceData(): {
	routeName: string;
	successfulExperiences: Set<string>;
} {
	return { ...previousRoute };
}

const splitViewContentExperience = (tracker: ExperienceTracker, event: ExperienceEvent) => {
	let name: ExperienceEvent['name'];

	if (event.attributes?.contentType === 'space') {
		name = SPACE_OVERVIEW_EXPERIENCE;

		const spaceKey = event.attributes?.spaceKey;
		if (typeof spaceKey === 'string' && isCompanyHubSpaceKey(spaceKey)) {
			name = FRONT_COVER_LOAD_EXPERIENCE;
		}
	} else {
		name = VIEW_PAGE_EXPERIENCE;
		if (event.action === 'taskStart') {
			startViewPageSessionExperience({
				tracker,
				attributes: { ...event.attributes },
				startTime: event.startTime,
				id: event.id,
			});
		}
	}

	forwardExperience(tracker, event, name);
};

/**
 * Extend the ExperienceEvent with Confluence-specific additional attributes based on available data and type of event.
 *
 * @param event ExperienceEvent
 * @returns ExperienceEvent with additional attributes
 */
const provideGenericAttributes = (event: ExperienceEvent) => {
	const errorTraceId =
		event.action === 'taskFail' && !!event?.error
			? getLogSafeErrorAttributes(event.error)?.traceId
			: undefined;

	// Expected that condition will be extended with && for other attributes added in the future
	if (!errorTraceId) {
		return event;
	}

	event.attributes = {
		...(!!errorTraceId && { traceId: errorTraceId }),
		...event.attributes,
	};

	return event;
};
